import { setUploadProgress } from '@/store/slices/poc/heliaSlice';
import { findUploadEntryByCid } from '@/store/utils/uploadProgress.utils';
import type { AppListenerEffectAPI } from '@/store/listeners/types';

const PROGRESS_HAP_START = 85;
const PROGRESS_HAP_RANGE = 10;
const PROGRESS_HAP_REGISTERING = 95;
const SILENCE_WATCHDOG_MS = 30000;
const HAP_CONNECTION_ERROR =
  'Lost data connection to HAP - your upload may still be processing. Try refreshing.';

const activeWatchdogs = new Map<string, ReturnType<AppListenerEffectAPI['fork']>>();

function stopWatchdog(uploadId: string): void {
  const task = activeWatchdogs.get(uploadId);
  if (task !== undefined) {
    task.cancel();
    activeWatchdogs.delete(uploadId);
  }
}

function restartWatchdog(
  listenerApi: AppListenerEffectAPI,
  args: { uploadId: string; fileName: string; cid: string; progress: number }
): void {
  stopWatchdog(args.uploadId);

  const task = listenerApi.fork(async(forkApi) => {
    try {
      await forkApi.delay(SILENCE_WATCHDOG_MS);
      listenerApi.dispatch(setUploadProgress({
        uploadId: args.uploadId,
        fileName: args.fileName,
        cid: args.cid,
        stage: 'error',
        progress: args.progress,
        error: HAP_CONNECTION_ERROR,
      }));
    } finally {
      if (activeWatchdogs.get(args.uploadId) === task) {
        activeWatchdogs.delete(args.uploadId);
      }
    }
  });

  activeWatchdogs.set(args.uploadId, task);
}

function findUploadEntry(listenerApi: AppListenerEffectAPI, cid: string) {
  return findUploadEntryByCid(listenerApi.getState().helia.uploadProgress, cid);
}

export function handleReplicationStarted(
  listenerApi: AppListenerEffectAPI,
  args: { cid: string; totalBytes: number }
): void {
  const uploadEntry = findUploadEntry(listenerApi, args.cid);
  if (uploadEntry === undefined) {
    return;
  }

  restartWatchdog(listenerApi, {
    uploadId: uploadEntry.uploadId,
    fileName: uploadEntry.fileName,
    cid: args.cid,
    progress: PROGRESS_HAP_START,
  });

  listenerApi.dispatch(setUploadProgress({
    uploadId: uploadEntry.uploadId,
    fileName: uploadEntry.fileName,
    stage: 'hap-downloading',
    progress: PROGRESS_HAP_START,
    hapBytesTransferred: 0,
    hapTotalBytes: args.totalBytes,
  }));
}

export function handleReplicationProgress(
  listenerApi: AppListenerEffectAPI,
  args: {
    cid: string;
    stage: 'fetching' | 'registering';
    bytesTransferred: number;
    totalBytes: number;
  }
): void {
  const uploadEntry = findUploadEntry(listenerApi, args.cid);
  if (uploadEntry === undefined) {
    return;
  }

  const progress =
    args.stage === 'fetching'
      ? Math.min(
          PROGRESS_HAP_START +
            (args.totalBytes > 0
              ? (args.bytesTransferred / args.totalBytes) * PROGRESS_HAP_RANGE
              : PROGRESS_HAP_RANGE),
          PROGRESS_HAP_REGISTERING
        )
      : PROGRESS_HAP_REGISTERING;

  restartWatchdog(listenerApi, {
    uploadId: uploadEntry.uploadId,
    fileName: uploadEntry.fileName,
    cid: args.cid,
    progress,
  });

  listenerApi.dispatch(setUploadProgress({
    uploadId: uploadEntry.uploadId,
    fileName: uploadEntry.fileName,
    stage: args.stage === 'fetching' ? 'hap-downloading' : 'hap-registering',
    progress,
    hapBytesTransferred: args.bytesTransferred,
    hapTotalBytes: args.totalBytes,
  }));
}

export function handleReplicationFailed(
  listenerApi: AppListenerEffectAPI,
  args: { cid: string; reason: string }
): void {
  const uploadEntry = findUploadEntry(listenerApi, args.cid);
  if (uploadEntry === undefined) {
    return;
  }

  stopWatchdog(uploadEntry.uploadId);
  listenerApi.dispatch(setUploadProgress({
    uploadId: uploadEntry.uploadId,
    fileName: uploadEntry.fileName,
    stage: 'error',
    progress: uploadEntry.progress,
    error: args.reason,
  }));
}

export function stopReplicationTrackingByCid(
  listenerApi: AppListenerEffectAPI,
  cid: string
): void {
  const uploadEntry = findUploadEntry(listenerApi, cid);
  if (uploadEntry !== undefined) {
    stopWatchdog(uploadEntry.uploadId);
  }
}

export function stopReplicationTrackingByUploadId(uploadId: string): void {
  stopWatchdog(uploadId);
}

export function stopAllReplicationTracking(): void {
  activeWatchdogs.forEach((task) => {
    task.cancel();
  });
  activeWatchdogs.clear();
}
